"use client";

import { useEffect } from "react";
import HeroSection from "./components/HeroSection";
import StickyBar from "./components/StickyBar";
import IntroSection from "./components/IntroSection";
import NeighborhoodStats from "@/components/feature/NeighborhoodStats";
import RepairServicesGrid from "@/components/feature/RepairServicesGrid";
import ProcessTimeline from "./components/ProcessTimeline";
import TestimonialsSection from "./components/TestimonialsSection";
import NeighborsSection from "./components/NeighborsSection";
import ScopeDisclaimer from "@/components/feature/ScopeDisclaimer";
import NeighborhoodCta from "@/components/feature/NeighborhoodCta";
import QuartierCrossLinks from "@/components/feature/QuartierCrossLinks";
import { injectMetaTags } from "@/lib/seoHelpers";
import FaqSection from "@/components/feature/FaqSection";
import RelatedServicesBlock from "@/components/feature/RelatedServicesBlock";
import NearbyAreasBlock from "@/components/feature/NearbyAreasBlock";
import AboutAreaSection from "@/components/feature/AboutAreaSection";
import { useLanguage } from "@/i18n/LanguageContext";
import { useHreflang } from "@/i18n/useHreflang";

const neighborhood = "Brickell";
const zips = ["33129", "33130", "33131"];

export default function BrickellPage() {
  const { t, currentLang } = useLanguage();
  useHreflang();

  const brickellFaqs = [
    {
      question: t("hood.faq1Q"),
      answer: t("hood.faq1A"),
    },
    {
      question: t("hood.faq2Q"),
      answer: t("hood.faq2A"),
    },
    {
      question: t("hood.faq3Q"),
      answer: t("hood.faq3A"),
    },
    {
      question: t("hood.faq4Q"),
      answer: t("hood.faq4A"),
    },
    {
      question: t("brickell.faq5Q"),
      answer: t("brickell.faq5A"),
    },
  ];

  useEffect(() => {
    document.title = currentLang === "es"
      ? "Reparación de Ventanas Brickell — Rascacielos y Condos"
      : "Brickell Window Repair | Hardware, Glass, Maintenance";

    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = currentLang === "es"
      ? "Especialistas en reparación de ventanas en Brickell. Servicio de condos de gran altura coordinado con administración. El mismo día en torres Brickell."
      : "Brickell window repair specialists. High-rise condo service coordinated with building management. Same-day across all Brickell towers.";

    const cleanupSeo = injectMetaTags({
      title: currentLang === "es"
        ? "Reparación de Ventanas Brickell — Rascacielos y Condos"
        : "Brickell Window Repair | Hardware, Glass, Maintenance",
      description: currentLang === "es"
        ? "Especialistas en reparación de ventanas en Brickell. Servicio de condos de gran altura coordinado con administración. El mismo día en torres Brickell."
        : "Brickell window repair specialists. High-rise condo service coordinated with building management. Same-day across all Brickell towers.",
      url: "https://impact-window-repair-miami.com/impact-windows-brickell/",
      image: "https://impact-window-repair-miami.com/og/og-brickell.jpg",
      imageAlt: currentLang === "es"
        ? "Reparación de ventanas Brickell Miami"
        : "Window repair Brickell Miami",
      breadcrumbs: [
        { name: "Home", item: "https://impact-window-repair-miami.com/" },
        { name: "Service Areas", item: "https://impact-window-repair-miami.com/service-areas/" },
        { name: "Brickell", item: "https://impact-window-repair-miami.com/impact-windows-brickell/" }
      ],
      type: "website",
    });

    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: currentLang === "es" ? "Reparación de Ventanas" : "Window Repair",
          inLanguage: currentLang === "es" ? "es-US" : "en-US",
          provider: {
            "@type": "LocalBusiness",
            name: "Windows and Doors Repair LLC",
            telephone: "+17863774452",
            email: "contact@impact-window-repair-miami.com",
            address: {
              "@type": "PostalAddress",
              streetAddress: "848 Brickell Ave, PH-5",
              addressLocality: "Miami",
              addressRegion: "FL",
              postalCode: "33131",
              addressCountry: "US",
            },
            geo: {
              "@type": "GeoCoordinates",
              latitude: 25.7617,
              longitude: -80.1918,
            },
            openingHoursSpecification: [{
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
              opens: "07:00",
              closes: "21:00",
            }],
            priceRange: "$$",
            paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
            currenciesAccepted: "USD",
            areaServed: [
              { "@type": "City", name: "Miami" },
              { "@type": "AdministrativeArea", name: "Miami-Dade County" },
              "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove",
              "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne",
              "South Miami", "North Miami Beach",
            ],
            knowsLanguage: ["en", "es"],
            knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
            slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          },
          areaServed: { "@type": "City", name: "Brickell, Miami-Dade, FL" },
          serviceType: "Window Repair",
          description: currentLang === "es"
            ? "Reparación de herrajes, vidrios y mantenimiento en Brickell."
            : "Hardware repair, glass replacement, and maintenance in Brickell.",
        },
        {
          "@type": "LocalBusiness",
          inLanguage: currentLang === "es" ? "es-US" : "en-US",
          name: "Windows and Doors Repair LLC",
          telephone: "+17863774452",
          email: "contact@impact-window-repair-miami.com",
          address: {
            "@type": "PostalAddress",
            streetAddress: "848 Brickell Ave, PH-5",
            addressLocality: "Miami",
            addressRegion: "FL",
            postalCode: "33131",
            addressCountry: "US",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 25.7617,
            longitude: -80.1918,
          },
          openingHoursSpecification: [{
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "07:00",
            closes: "21:00",
          }],
          priceRange: "$$",
          paymentAccepted: ["Cash", "Credit Card", "Debit Card"],
          currenciesAccepted: "USD",
          areaServed: [
            { "@type": "City", name: "Miami" },
            { "@type": "AdministrativeArea", name: "Miami-Dade County" },
            "Brickell", "Aventura", "Miami Beach", "Coral Gables", "Coconut Grove",
            "Doral", "Kendall", "Pinecrest", "Bay Harbor Islands", "Key Biscayne",
            "South Miami", "North Miami Beach",
          ],
          knowsLanguage: ["en", "es"],
          knowsAbout: ["Window Hardware Repair", "Sliding Door Repair", "Window Glass Replacement", "Weatherstripping", "Window Maintenance", "Emergency Board-Up"],
          slogan: "Honest Miami Window Repair. Same-Day. 30-Day Guarantee.",
          url: window.location.href,
        },
      ],
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "main-schema";
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage: currentLang === "es" ? "es-US" : "en-US",
      mainEntity: brickellFaqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.id = "faq-schema";
    faqScript.textContent = JSON.stringify(faqSchema);
    document.head.appendChild(faqScript);

    return () => {
      cleanupSeo();
      const s = document.getElementById("main-schema");
      if (s) document.head.removeChild(s);
      const fs = document.getElementById("faq-schema");
      if (fs) document.head.removeChild(fs);
    };
  }, [currentLang]);

  return (
    <main className="min-h-screen">
      <HeroSection
        zips={zips}
        h1={t("brickell.h1")}
        paragraph={t("brickell.subhead")}
      />
      <AboutAreaSection
        badge={currentLang === "es" ? "REPARACIÓN DE VENTANAS BRICKELL" : "BRICKELL WINDOW REPAIR"}
        imageSrc="https://placehold.co/520x380/064e3b/ffffff?text=BRICKELL+WINDOW+REPAIR"
        imageAlt={currentLang === "es"
          ? "Especialista en reparación de ventanas Brickell Miami en condo de gran altura"
          : "Brickell Miami high-rise condo window repair specialist working on patio slider"}
        heading={currentLang === "es"
          ? "Reparación de Ventanas para Condos y Rascacielos Brickell"
          : "Window Repair for Brickell Condos and High-Rises"}
        lead={t("brickell.intro")}
        bullets={
          currentLang === "es"
            ? [
                "Reparación de rodillos y cerraduras en puertas corredizas de balcón",
                "Servicio de herrajes coordinado con administración del edificio",
                "Diagnóstico el mismo día en torres de Brickell",
              ]
            : [
                "Roller and lock repair on balcony sliding doors",
                "Hardware service coordinated with building management",
                "Same-day diagnostic across Brickell towers",
              ]
        }
        closing={currentLang === "es"
          ? "La mayoría de reparaciones en Brickell programadas dentro de 24 horas con coordinación de acceso."
          : "Most Brickell repairs scheduled within 24 hours with building access coordination."}
      />
      <StickyBar name={neighborhood} zips={zips} />
      <IntroSection
        title={currentLang === "es" ? "Por Qué Brickell Necesita Reparación de Ventanas" : "Why Brickell Needs Window Repair"}
        paragraphs={
          currentLang === "es"
            ? [
                "Brickell está en un corredor de rascacielos expuesto directamente a ráfagas de viento del Atlántico y aire salino. Los herrajes se corroen más rápido en condos costeros, y los daños de vidrio por escombros son comunes en pisos superiores.",
                "Los entornos con aire salino corroen cerraduras, pestillos y balances en 5-7 años. Reemplazamos herrajes gastados con alternativas de acero inoxidable de calidad marina que duran más en rascacielos costeros.",
                "La mayoría de los HOA de Brickell requieren trabajo en horarios de silencio y proveedores aprobados. Nuestros técnicos trabajan discretamente y respetan las políticas del edificio.",
              ]
            : [
                "Brickell sits in a high-rise corridor directly exposed to Atlantic wind gusts and salt air. Window hardware corrodes faster in coastal condos, and glass damage from debris is common on upper floors.",
                "Salt-air environments corrode locks, latches, and balances within 5–7 years. We replace worn hardware with marine-grade stainless alternatives that last longer in coastal high-rises.",
                "Most Brickell HOAs require quiet-hours work and pre-approved vendors. Our techs work discreetly and respect building policies for all repair visits.",
              ]
        }
        imageUrl="https://static.readdy.ai/image/60b7a1bdc124301cce40c5bb19c672c9/9ef0a52e93d9d779fa183f9e9fc74c67.jpeg"
      />
      <NeighborhoodStats />
      <RepairServicesGrid heading={t("hood.commonH2")} />
      <ScopeDisclaimer neighborhood={neighborhood} />
      <ProcessTimeline />
      <TestimonialsSection
        testimonials={[
          {
            name: "Elena Volkov",
            zip: "33131",
            tag: "Hardware Fix",
            quote: "Our 38th-floor condo had a broken crank operator on three windows. Tech arrived same day, replaced the hardware, and cleaned the tracks. Quiet and professional.",
          },
          {
            name: "Devontae Bryant",
            zip: "33129",
            tag: "Glass Replacement",
            quote: "Broken pane in our townhouse kitchen. They measured, ordered non-impact glass, and installed it in the existing frame within two days. Fair pricing upfront.",
          },
          {
            name: "Anika Sharma",
            zip: "33130",
            tag: "Weatherstripping",
            quote: "Water was leaking through the balcony door every storm. They replaced the weatherstripping and realigned the track. No more leaks.",
          },
        ]}
      />
      <NeighborsSection
        neighbors={[
          { name: "Coral Gables", zip: "33134", slug: "coral-gables" },
          { name: "Coconut Grove", zip: "33133", slug: "coconut-grove" },
          { name: "Key Biscayne", zip: "33149", slug: "key-biscayne" },
          { name: "Aventura", zip: "33180", slug: "aventura" },
        ]}
      />
      <FaqSection items={brickellFaqs} heading={t("hood.commonFaqH2")} />
      <RelatedServicesBlock neighborhood={neighborhood} />
      <NearbyAreasBlock neighborhood={neighborhood} neighbors={[
        { name: "Coconut Grove", slug: "coconut-grove" },
        { name: "Coral Gables", slug: "coral-gables" },
        { name: "Key Biscayne", slug: "key-biscayne" },
      ]} />
      <QuartierCrossLinks />
      <NeighborhoodCta neighborhood={neighborhood} heading={t("hood.commonCta")} />
    </main>
  );
}